/**
 * AppShell — lightweight screen-wrapper and layout utilities.
 *
 * Exports:
 *   useAppLayout      — provides compatibility layout values for screens
 *   PhoneShell        — simple viewport-sized app surface
 *   AppScreen         — screen-wrapper: SafeAreaView (top) + plain View
 */
import React, { useEffect, useState } from 'react';
import { View, StyleSheet, Platform } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { Colors } from '../theme';

// Safe area and viewport are fully handled via static CSS (patch-dist.js + env()).
// No JS polling needed — returning true immediately eliminates the post-mount
// re-render that was causing the visible layout shift on the Assistant screen.
export function useWebViewportReady() {
  return true;
}

/** Keeps the UI honest when the PWA loses connectivity. */
export function useOnlineStatus() {
  const [online, setOnline] = useState(() => typeof navigator === 'undefined' || navigator.onLine !== false);

  useEffect(() => {
    if (Platform.OS !== 'web' || typeof window === 'undefined') return;
    const update = () => setOnline(window.navigator.onLine !== false);
    update();
    window.addEventListener('online', update, { passive: true });
    window.addEventListener('offline', update, { passive: true });
    return () => {
      window.removeEventListener('online', update);
      window.removeEventListener('offline', update);
    };
  }, []);

  return online;
}

/**
 * Returns compatibility values for screens that still consume this hook.
 * There is no bottom navigation surface anymore, so it must not reserve a
 * visible strip or spacer below the app content.
 *
 * tabBarBottom      — bottom padding to add to the tab-bar View itself
 * bottomContentPadding — native bottom padding to add to ScrollView content
 * bottomContentStyle   — platform-aware ScrollView padding style
 * bottomSpacerStyle    — platform-aware spacer style for short screens
 */
export function useAppLayout() {
  const tabBarBottom = 0;
  const bottomContentPadding = 0;
  const bottomContentStyle = { paddingBottom: 0 };
  const bottomSpacerStyle = { height: 0 };
  return { tabBarBottom, bottomContentPadding, bottomContentStyle, bottomSpacerStyle };
}

interface AppScreenProps {
  children: React.ReactNode;
  style?: any;
  safeStyle?: any;
}

interface PhoneShellProps {
  children: React.ReactNode;
}

/**
 * The shell owns the app surface, not the browser's scroll position. It has
 * no artificial phone frame, fixed document height, or global clipping.
 */
export function PhoneShell({ children }: PhoneShellProps) {
  return <View style={styles.phoneShell}>{children}</View>;
}

/**
 * AppScreen — top safe-area wrapper. The bottom edge is not reserved because
 * the app no longer renders a bottom navigation bar.
 */
export function AppScreen({ children, style, safeStyle }: AppScreenProps) {
  return (
    <SafeAreaView
      style={[styles.safe, safeStyle, Platform.OS === 'web' && styles.safeWeb]}
      edges={Platform.OS === 'web' ? [] : ['top']}
    >
      <View style={[styles.content, Platform.OS === 'web' && styles.contentWeb, style]}>
        {children}
      </View>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  safe: {
    flex: 1,
    width: '100%',
    minWidth: 0,
    overflow: 'visible',
    // The safe-area/status-bar inset belongs to the app canvas, not a white
    // sheet. Keeping it on the same background avoids a visible strip above
    // every authenticated screen on iOS/PWA.
    backgroundColor: Colors.bg,
  },
  safeWeb: {
    paddingTop: 'env(safe-area-inset-top, 0px)' as any,
    boxSizing: 'border-box',
    backgroundColor: Colors.bg,
  },
  content: {
    flex: 1,
    width: '100%',
    minWidth: 0,
    minHeight: 0,
    backgroundColor: Colors.bg,
  },
  contentWeb: {},
  phoneShell: {
    flex: 1,
    width: '100%',
    minHeight: Platform.OS === 'web' ? ('100dvh' as any) : '100%',
    maxWidth: Platform.OS === 'web' ? 430 : undefined,
    alignSelf: 'center',
    position: 'relative',
    overflow: 'visible',
    minWidth: 0,
    boxSizing: 'border-box',
    backgroundColor: Colors.bg,
  },
});
